import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getFixedNumber, loginUserDetails, } from '../../../../../components/Common/CommonFunction';
import { numberWithCommas } from '../../../../../Reports/Report_common_function';



const SaleSummaryThermalPrintReport = (tableData) => {
    // ⭐ SAFE FALLBACK — never let a missing/undefined payload crash the report
    tableData = tableData || {};
    // ⭐ zero-item case — same full report layout, "No Sales Found" fills the
    // row space where line items / payment rows would normally go.
    const hasItems = Array.isArray(tableData) && tableData.length > 0;

    const rows = hasItems
        ? Rows_1(tableData)
        : [['No Sales Found', '', '', 'Span']];

    const PayMentMode_rows = hasItems
        ? PayMentMode_Rows(tableData)
        : [['No Sales Found', '', 'Span']];

    const address = loginUserDetails() || {};

    // ⭐ SAFE FALLBACK — GSTIN was never present on tableData from the API,
    // so tableData.GSTIN rendered the literal string "undefined". Fall back
    // through address.GSTIN, then finally an empty string so the line just
    // stays blank instead of showing "undefined".
    const gstinDisplay = tableData.GSTIN || address.GSTIN || '';

    // ⭐ ALIGNMENT FIX — page is 80mm wide. Text drawn with doc.text() does
    // NOT auto-wrap; a long supplier name was running off the right edge of
    // the page. splitTextToSize() wraps it inside a safe width instead.
    const PAGE_WIDTH = 80;
    const PAGE_CENTER_X = PAGE_WIDTH / 2;
    const SAFE_TEXT_WIDTH = 70; // 5mm margin each side, matches table margins

    const drawCenteredWrapped = (docRef, text, y, fontSize) => {
        docRef.setFontSize(fontSize);
        const lines = docRef.splitTextToSize(String(text ?? ''), SAFE_TEXT_WIDTH);
        docRef.text(lines, PAGE_CENTER_X, y, { align: 'center' });
        return lines.length;
    };

    // ⭐ 2-PAGE FIX — previously only the ITEM table's height was measured
    // (tempDoc), so the real page was sized too short to also fit the
    // PAYMENT MODE table that gets drawn after it — pushing that second
    // table onto a new page. renderFullReport() below draws the ENTIRE
    // report (header, address, item table, payment table) in one pass, so
    // whichever doc it's given — tall temp doc or final doc — ends up with
    // an IDENTICAL layout. We run it once against a tall temp doc purely to
    // measure the true total content height, then build the real doc at
    // exactly that height and run it again for real. Same content, same
    // order, same styling — just correctly sized so it never splits pages.
    const renderFullReport = (docRef) => {
        docRef.setFont('Tahoma');

        let y = 13;
        y += drawCenteredWrapped(docRef, 'Sale Summary Report', y, 18) * 6.5;
        y += drawCenteredWrapped(docRef, tableData.SupplierName || '', y, 12) * 5.2;
        y += drawCenteredWrapped(docRef, gstinDisplay, y, 12) * 5.2;
        y += drawCenteredWrapped(docRef, tableData.Date || '', y, 10) * 4.5;

        docRef.autoTable({
            startY: y + 3,
            margin: { left: 5, right: 5 },
            body: [
                [{ content: `${address.PartyAddress || ''}` }]
            ],
            styles: {
                fontSize: 9,
                halign: 'center',
                cellPadding: 1,
                textColor: [0, 0, 0]
            },
            theme: 'plain'
        });

        const afterAddressY = docRef.autoTable.previous.finalY;

        docRef.autoTable({
            startY: afterAddressY + 2,
            margin: { left: 5, right: 5 },
            theme: 'grid',
            head: [['Item', 'Qty', 'Rate', 'Amount']],
            body: rows,
            columnStyles: {
                0: { columnWidth: 30 },
                1: { columnWidth: 12, halign: 'right' },
                2: { columnWidth: 12, halign: 'right' },
                3: { columnWidth: 15, halign: 'right' },
            },
            bodyStyles: {
                fontSize: 7,
                textColor: [30, 30, 30],
                cellPadding: 1,
                lineColor: [6, 3, 1]
            },
            showHead: 'firstPage',
            headerStyles: {
                cellPadding: 2,
                lineWidth: 0.1,
                valign: 'top',
                fontStyle: 'bold',
                halign: 'center',
                fillColor: "white",
                textColor: [0, 0, 0],
                fontSize: 8,
                rowHeight: 10,
                lineColor: [6, 3, 1]
            },
            didParseCell: (data1) => {
                if (data1.row.cells[3].raw === "Span") {
                    data1.row.cells[0].colSpan = 4;
                    data1.row.cells[0].styles.halign = "left";
                    data1.row.cells[0].styles.fontSize = 8;
                    data1.row.cells[0].styles.fontStyle = "bold";
                }
            },
            styles: { fontSize: 8, cellPadding: 2 }
        });

        const Privious_Table = docRef.autoTable.previous.finalY;
        docRef.autoTable({
            startY: Privious_Table + 5,
            margin: { left: 5, right: 5 },
            theme: 'grid',
            head: [['Payment Mode', 'Amount']],
            showHead: 'firstPage',
            headerStyles: {
                cellPadding: 2,
                lineWidth: 0.1,
                valign: 'top',
                fontStyle: 'bold',
                halign: 'center',
                fillColor: "white",
                textColor: [0, 0, 0],
                fontSize: 8,
                rowHeight: 5,
                lineColor: [6, 3, 1]
            },
            columnStyles: {
                0: { columnWidth: 42 },
                1: { columnWidth: 27, halign: 'right' },
            },
            body: PayMentMode_rows,
            styles: { fontSize: 8, cellPadding: 2 },
            bodyStyles: {
                fontSize: 9,
                textColor: [30, 30, 30],
                cellPadding: 1,
                lineColor: [6, 3, 1]
            },
            didParseCell: (data1) => {
                if (data1.row.cells[1] && data1.row.cells[1].raw === "Span") {
                    data1.row.cells[0].colSpan = 2;
                    data1.row.cells[0].styles.halign = "center";
                    data1.row.cells[0].styles.fontStyle = "bold";
                    return;
                }
                if (data1.section === "body" && data1.column.index === 0) {
                    const boldModes = ["Card", "Cash", "UPI", "Partially", "Loyalty", "Total", "Gift"];
                    // ⭐ SAFE FALLBACK — guard startsWith in case cell.raw isn't a string
                    if (boldModes.some(mode => String(data1.cell.raw || '').startsWith(mode))) {
                        data1.cell.styles.fontStyle = "bold";
                    }
                }
            }
        });

        return docRef.autoTable.previous.finalY;
    };

    // Step 1: Render the FULL report (header + both tables) into a tall
    // offscreen doc purely to measure the true total content height.
    const tempDoc = new jsPDF({
        unit: 'mm',
        format: [80, 10000],
        orientation: 'portrait'
    });
    const totalContentHeight = renderFullReport(tempDoc);

    // Step 2: Build the real doc at exactly that height (+ small bottom
    // padding) so everything — header, address, item table, payment table —
    // fits on ONE page, correctly centered, nothing clipped or pushed over.
    const doc = new jsPDF({
        unit: 'mm',
        format: [80, totalContentHeight + 5],
        orientation: 'portrait'
    });
    renderFullReport(doc);

    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
};









export const Rows_1 = (data) => {

    // ⭐ SAFE FALLBACK — if data isn't an array (missing/undefined/wrong shape),
    // treat it as empty instead of throwing on .sort()/.reduce()
    data = Array.isArray(data) ? data : (Array.isArray(data?.Data) ? data.Data : []);

    data.sort((firstItem, secondItem) => firstItem.GSTPercentage - secondItem.GSTPercentage);

    const returnArr = [];
    let Gst = 0
    let totalGst = 0
    let totalAmount = 0
    let totalQuantity = 0
    let GSTPercentage = 0
    let finalTotal = 0
    let finalTotalGST = 0
    let finalBasicAmount = 0
    let finalDiscountAmount = 0

    const groupedItems = data.reduce((accumulator, currentItem) => {
        const { ItemName, BaseItemUnitQuantity, Rate, GrandTotal, GSTPercentage, GSTAmount, BasicAmount, DiscountAmount, Amount, MRPValue, id, RoundOffAmount } = currentItem;

        const key = ItemName + '_' + MRPValue;
        if (accumulator[key]) {
            accumulator[key].BaseItemUnitQuantity += getFixedNumber(BaseItemUnitQuantity, 3);
            accumulator[key].GrandTotal += getFixedNumber(GrandTotal, 3);
            accumulator[key].GSTAmount += getFixedNumber(GSTAmount, 3);
            accumulator[key].BasicAmount += getFixedNumber(BasicAmount, 3);
            accumulator[key].DiscountAmount += getFixedNumber(DiscountAmount, 3);
            accumulator[key].Amount += getFixedNumber(Amount, 3);
            accumulator[key].RoundOffAmount += RoundOffAmount;


        } else {
            accumulator[key] = {
                Rate, ItemName, GrandTotal: getFixedNumber(GrandTotal, 3), MRPValue,
                BaseItemUnitQuantity: getFixedNumber(BaseItemUnitQuantity, 3), GSTPercentage: GSTPercentage, GSTAmount: getFixedNumber(GSTAmount, 3), BasicAmount: getFixedNumber(BasicAmount, 3), DiscountAmount: getFixedNumber(DiscountAmount, 3), Amount: getFixedNumber(Amount, 3), RoundOffAmount: Number(RoundOffAmount), id: id
            };
        }
        return accumulator;
    }, {});

    const TotalItemlength = Object.values(groupedItems).length;
    data["TotalItemlength"] = TotalItemlength;
    const uniqueMap = new Map();
    Object.values(groupedItems).forEach((element, key) => {

        // if (!uniqueMap.has(element.id)) {
        //     uniqueMap.set(element.id, element.RoundOffAmount);
        // }
        finalTotal += element.Amount;
        finalTotalGST += element.GSTAmount;
        finalBasicAmount += element.BasicAmount;
        finalDiscountAmount += element.DiscountAmount;

        const tableitemRow = [
            `${element.ItemName}`,
            `${(getFixedNumber(element.BaseItemUnitQuantity, 2)).toFixed(2)}`,
            `${(getFixedNumber(element.MRPValue, 2)).toFixed(2)}`,
            `${(getFixedNumber(element.Amount, 2)).toFixed(2)}`,
        ];
        // returnArr.push(tableitemRow);

        function totalrow() {
            return [
                `GST ${(parseFloat(GSTPercentage))}%\nTotal GST: ${numberWithCommas(Number(totalGst).toFixed(2))}\nTotal:${numberWithCommas(Number(totalAmount).toFixed(2))} `,
                ``,
                ``,
                `Span`,
            ];
        };

        function totalLots() {


            totalQuantity = getFixedNumber(totalQuantity, 3) + getFixedNumber(element.BaseItemUnitQuantity, 3)
            totalAmount = getFixedNumber(totalAmount, 3) + getFixedNumber(element.Amount, 3)
            totalGst = getFixedNumber(totalGst, 3) + getFixedNumber(element.GSTAmount, 3)
            GSTPercentage = Number(element.GSTPercentage)
        };

        if (Gst === 0) { Gst = element.GSTPercentage };

        if ((Gst === element.GSTPercentage)) {
            returnArr.push(tableitemRow)
            totalLots()
        }
        else {

            returnArr.push(totalrow());
            returnArr.push(tableitemRow);
            totalGst = 0;
            totalAmount = 0;
            totalQuantity = 0;
            Gst = element.GSTPercentage;
            totalLots()

        }

        if (key === Object.keys(groupedItems).length - 1) {
            returnArr.push(totalrow());
        }
    })

    // ⭐ FIX — `returnArr.length - 1` is truthy even when length is 0 (0-1 = -1,
    // which is truthy), so the "GST Total / Amount" summary row was appearing
    // even with zero items. Changed to an explicit length check so an empty
    // result set stays empty instead of showing a misleading "0.00" summary.
    if (returnArr.length > 0) {
        returnArr.push([
            `GST Total: ${numberWithCommas(Number(finalTotalGST).toFixed(2))}            Amount: ${numberWithCommas(Number(finalTotal).toFixed(2))}`,
            ``,
            ``,
            `Span`,
        ]);
    }
    const roundOffByInvoice = new Map();
    for (const row of data) {
        const invId = row.id;
        const ro = Number(row.RoundOffAmount) || 0;
        if (!roundOffByInvoice.has(invId)) {
            roundOffByInvoice.set(invId, ro);
        }
    }
    const dayRoundOff = [...roundOffByInvoice.values()].reduce((sum, v) => sum + v, 0);

    if (returnArr.length > 0) {
        returnArr.push([
            // `Round Off: ${([...uniqueMap.values()].reduce((sum, amt) => sum + amt, 0)).toFixed(2)}`,
            `Round Off: ${dayRoundOff.toFixed(2)}`,
            ``,
            ``,
            `Span`,
        ]);
    }

    if (returnArr.length > 0) {
        returnArr.push([
            `Discount Amount: ${numberWithCommas(Number(finalDiscountAmount).toFixed(2))}           `,
            ``,
            ``,
            `Span`,
        ]);
    }


    return returnArr;
}




export const PayMentMode_Rows = (data) => {
    const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.Data)
            ? data.Data
            : [];

    const modeTotals = {};
    const invoiceMap = new Map();

    // ---------------------------------------------------------
    // STEP 1: Keep only ONE payment record per invoice
    // ---------------------------------------------------------
    rows.forEach((item, index) => {

        const invoiceKey =
            item.id !== null && item.id !== undefined
                ? `id:${item.id}`
                : item.FullInvoiceNumber
                    ? `invoice:${item.FullInvoiceNumber}`
                    : `row:${index}`;

        if (!invoiceMap.has(invoiceKey)) {
            invoiceMap.set(invoiceKey, { ...item });
        } else {
            // If payment information is missing in the first row,
            // pick it from another row of the same invoice.
            const existing = invoiceMap.get(invoiceKey);

            if (!existing.PaymentType && item.PaymentType) {
                existing.PaymentType = item.PaymentType;
            }

            if (!existing.PaymentMode1 && item.PaymentMode1) {
                existing.PaymentMode1 = item.PaymentMode1;
            }

            if (
                !(Number(existing.PaymentMode1Amount) > 0) &&
                Number(item.PaymentMode1Amount) > 0
            ) {
                existing.PaymentMode1Amount = item.PaymentMode1Amount;
            }

            if (!existing.PaymentMode2 && item.PaymentMode2) {
                existing.PaymentMode2 = item.PaymentMode2;
            }

            if (
                !(Number(existing.PaymentMode2Amount) > 0) &&
                Number(item.PaymentMode2Amount) > 0
            ) {
                existing.PaymentMode2Amount = item.PaymentMode2Amount;
            }

            if (
                !(Number(existing.GrandTotal) > 0) &&
                Number(item.GrandTotal) > 0
            ) {
                existing.GrandTotal = item.GrandTotal;
            }
        }
    });

    // ---------------------------------------------------------
    // STEP 2: Add payment amount to the correct payment mode
    // ---------------------------------------------------------
    const addPaymentMode = (mode, amount) => {

        const cleanMode = String(mode || "").trim();
        const numericAmount = Number(amount);

        if (
            !cleanMode ||
            !Number.isFinite(numericAmount) ||
            numericAmount <= 0
        ) {
            return;
        }

        modeTotals[cleanMode] = getFixedNumber(
            (modeTotals[cleanMode] || 0) + numericAmount,
            2
        );
    };

    // ---------------------------------------------------------
    // STEP 3: Add payments to the payment modes
    // ---------------------------------------------------------

    // True only when EVERY row carries an invoice identifier
    const hasInvoiceKey =
        rows.length > 0 &&
        rows.every(
            (r) =>
                (r.id !== null && r.id !== undefined) ||
                r.FullInvoiceNumber
        );

    if (!hasInvoiceKey) {
        // No bill identifier in the data, so rows of one bill can't be
        // grouped. Each item's Amount is added to its bill's payment mode;
        // a bill's item amounts add up to its payment amount.
        rows.forEach((item) => {
            const itemAmount = Number(item.Amount) || 0;
            const mode1 = String(item.PaymentMode1 || item.PaymentType || "").trim();
            const mode2 = String(item.PaymentMode2 || "").trim();
            const amt1 = Number(item.PaymentMode1Amount) || 0;
            const amt2 = Number(item.PaymentMode2Amount) || 0;

            if (String(item.PaymentType || "").trim() === "Partially" && mode2 && amt1 + amt2 > 0) {
                // split-payment bill: share the item amount in the payment ratio
                addPaymentMode(mode1, (itemAmount * amt1) / (amt1 + amt2));
                addPaymentMode(mode2, (itemAmount * amt2) / (amt1 + amt2));
            } else {
                addPaymentMode(mode1, itemAmount);
            }
        });
    } else {
        // Invoice id is present: your existing logic, unchanged
        invoiceMap.forEach((item) => {

            const paymentType = String(item.PaymentType || "").trim();
            const paymentMode1 = String(item.PaymentMode1 || "").trim();
            const paymentMode2 = String(item.PaymentMode2 || "").trim();
            const paymentMode1Amount = Number(item.PaymentMode1Amount) || 0;
            const paymentMode2Amount = Number(item.PaymentMode2Amount) || 0;

            if (paymentType === "Partially") {
                addPaymentMode(paymentMode1, paymentMode1Amount);
                addPaymentMode(paymentMode2, paymentMode2Amount);
            }
            else if (paymentMode1 && paymentMode1Amount > 0) {
                addPaymentMode(paymentMode1, paymentMode1Amount);
            }
            else if (paymentType) {
                const fallbackAmount =
                    Number(item.GrandTotal) || Number(item.Amount) || 0;
                addPaymentMode(paymentType, fallbackAmount);
            }
        });
    }
    // ---------------------------------------------------------
    // STEP 4: Prepare report rows
    // ---------------------------------------------------------

    const preferredOrder = [
        "UPI",
        "Cash",
        "Card",
        "Loyalty"
    ];

    const orderedModes = [
        ...preferredOrder.filter(
            (mode) =>
                Object.prototype.hasOwnProperty.call(
                    modeTotals,
                    mode
                )
        ),

        ...Object.keys(modeTotals).filter(
            (mode) => !preferredOrder.includes(mode)
        )
    ];

    const returnArr = [];

    let Total = 0;

    orderedModes.forEach((mode) => {

        const amount = getFixedNumber(
            modeTotals[mode] || 0,
            2
        );

        Total += amount;

        returnArr.push([
            mode,
            numberWithCommas(
                amount.toFixed(2)
            ),
            "Span"
        ]);
    });

    // ---------------------------------------------------------
    // STEP 5: Grand Total
    // ---------------------------------------------------------

    Total = getFixedNumber(Total, 2);

    returnArr.push([
        "Total",
        numberWithCommas(
            Total.toFixed(2)
        ),
        "Span"
    ]);

    return returnArr;
};






export default SaleSummaryThermalPrintReport;